import { useEffect } from "react";

import {
  Autocomplete as MuiAutocomplete,
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  Popper,
  Paper,
} from "@mui/material";
import { useState } from "react";
import Button from "./Button";
import TextInput from "./TextInput";
import { useTranslation } from "react-i18next";
import FieldLoader from "../Atom/FieldLoader";

export default function AutoComplete(props) {
  const { t } = useTranslation();

  const properties = {
    ...props,
    label: t(`FORM.FIELD.${props.name}`),
  };

  const { width, required, error, helperText, options, disabled, sx } =
    properties;

  return (
    <FormControl
      variant="outlined"
      sx={{
        width: width || "100%",
        ...sx,
      }}
      required={required}
      {...(error && { error: true })}
      {...((disabled || options.length === 0) && { disabled: true })}
    >
      <AutoCompleteComponent {...properties} />

      {options.length === 0 && (
        <Box
          display={"flex"}
          justifyContent={"center"}
          paddingTop={1}
          height={"100%"}
          width={"100%"}
          position={"absolute"}
        >
          <FieldLoader />
        </Box>
      )}

      {error && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

function AutoCompleteComponent(props) {
  let {
    name,
    label,
    value,
    onChange,
    options,
    disabled,
    error,
    isMobile,
    required,
    getOptionLabel,
  } = props;

  const [open, setOpen] = useState(false);

  const [selectedValue, setSelectedValue] = useState(value);
  const [inputValue, setInputValue] = useState(
    selectedValue ? selectedValue.label : ""
  );

  function handleChange(e, newValue) {
    setSelectedValue(newValue);
  }

  function onInputChange(e) {
    const { value } = e.target;
    setInputValue(value);
  }

  useEffect(() => {
    if (selectedValue !== value) {
      onChange({
        target: {
          name: name,
          value: selectedValue ?? "",
        },
      });
    }
  }, [selectedValue]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <Box
      sx={{
        ...(isMobile &&
          open && {
            backgroundColor: "white",
            padding: 1,
            zIndex: 2000,
            left: 0,
            position: "fixed",
            top: 0,
            width: "100%",
          }),
      }}
    >
      <MuiAutocomplete
        label={label}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        name={name}
        getOptionKey={(option) =>
          option ? option.label + "-" + option.value : ""
        }
        getOptionLabel={getOptionLabel}
        value={selectedValue}
        onChange={handleChange}
        {...((disabled || options.length === 0) && { disabled: true })}
        options={options}
        PaperComponent={(props) => (
          <Paper
            elevation={12}
            {...props}
            style={{
              marginTop: ".5em",
              marginBottom: ".5em",
              border: "2px solid blue",
            }}
          />
        )}
        PopperComponent={(props) => (
          <Popper
            {...props}
            style={{
              ...props?.style,
              ...(isMobile && open
                ? {
                    width: "100%",
                    display: "grid",
                    gap: "10px",
                    paddingLeft: "1em",
                    paddingRight: "1em",
                  }
                : {
                    width: "fit-content",
                  }),
            }}
            placement="bottom-start"
          />
        )}
        {...(isMobile && open && { freeSolo: true })}
        renderOption={(props, option, { index }) =>
          index < 20 && (
            <li {...props} key={index}>
              {getOptionLabel(option)}
            </li>
          )
        }
        renderInput={(params) => {
          return (
            <TextInput
              {...params}
              name={name}
              value={inputValue}
              onChange={onInputChange}
              required={required}
              error={error}
              {...(isMobile &&
                open && {
                  sx: { width: "100%" },
                  InputProps: {
                    ...params.InputProps,
                    sx: { width: "100%" },
                    startAdornment: (
                      <InputAdornment position="start">
                        <Button onClick={() => setOpen(false)} label="X" />
                      </InputAdornment>
                    ),
                  },
                })}
            />
          );
        }}
      />
    </Box>
  );
}
